import { useState, useEffect } from "react";
import { API } from "../App";
import Layout from "../components/Layout";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Package, 
  Plus, 
  Trash2, 
  CreditCard,
  Calendar,
  CheckCircle,
  Loader2
} from "lucide-react";

const AdminSubscriptions = () => {
  const [plans, setPlans] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPlanOpen, setIsPlanOpen] = useState(false);
  const [isBundleOpen, setIsBundleOpen] = useState(false);
  
  const [planForm, setPlanForm] = useState({
    name: "",
    description: "",
    price: 0,
    billing_cycle: "monthly",
    features: ""
  });

  const [bundleForm, setBundleForm] = useState({
    name: "",
    description: "",
    course_ids: [],
    price: 0,
    discount_percent: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, bundlesRes, coursesRes] = await Promise.all([
        axios.get(`${API}/subscription-plans`, { withCredentials: true }),
        axios.get(`${API}/bundles`, { withCredentials: true }),
        axios.get(`${API}/courses`, { withCredentials: true })
      ]);
      setPlans(plansRes.data);
      setBundles(bundlesRes.data);
      setCourses(coursesRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlan = async () => {
    if (!planForm.name || !planForm.price) {
      toast.error("Please fill in required fields");
      return;
    }

    try {
      await axios.post(`${API}/subscription-plans`, {
        ...planForm,
        features: planForm.features.split('\n').filter(f => f.trim())
      }, { withCredentials: true });
      
      toast.success("Subscription plan created!");
      setIsPlanOpen(false);
      setPlanForm({ name: "", description: "", price: 0, billing_cycle: "monthly", features: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to create plan");
    }
  };

  const handleCreateBundle = async () => {
    if (!bundleForm.name || !bundleForm.course_ids.length) {
      toast.error("Please fill in required fields and select courses");
      return;
    }

    try {
      await axios.post(`${API}/bundles`, bundleForm, { withCredentials: true });
      toast.success("Bundle created!");
      setIsBundleOpen(false);
      setBundleForm({ name: "", description: "", course_ids: [], price: 0, discount_percent: 0 });
      fetchData();
    } catch (error) {
      toast.error("Failed to create bundle");
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!window.confirm("Deactivate this subscription plan?")) return;
    try {
      await axios.delete(`${API}/subscription-plans/${planId}`, { withCredentials: true });
      toast.success("Plan deactivated");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete plan");
    }
  };

  const handleDeleteBundle = async (bundleId) => {
    if (!window.confirm("Deactivate this bundle?")) return;
    try {
      await axios.delete(`${API}/bundles/${bundleId}`, { withCredentials: true });
      toast.success("Bundle deactivated");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete bundle");
    }
  };

  const toggleCourseInBundle = (courseId) => {
    setBundleForm(prev => ({
      ...prev,
      course_ids: prev.course_ids.includes(courseId)
        ? prev.course_ids.filter(id => id !== courseId)
        : [...prev.course_ids, courseId]
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-700"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 animate-fade-in" data-testid="admin-subscriptions">
        {/* Header */}
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: 'Outfit' }}>
            Subscriptions & Bundles
          </h1>
          <p className="text-stone-600 mt-1">Manage subscription plans and course bundles</p>
        </div>

        <Tabs defaultValue="plans" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="plans">Subscription Plans</TabsTrigger>
            <TabsTrigger value="bundles">Course Bundles</TabsTrigger>
          </TabsList>

          {/* Subscription Plans Tab */}
          <TabsContent value="plans">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>
                Subscription Plans ({plans.length})
              </h2>
              <Dialog open={isPlanOpen} onOpenChange={setIsPlanOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-violet-700 hover:bg-violet-800 rounded-full" data-testid="create-plan-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Plan
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Outfit' }}>Create Subscription Plan</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label>Plan Name *</Label>
                      <Input
                        value={planForm.name}
                        onChange={(e) => setPlanForm({ ...planForm, name: e.target.value })}
                        placeholder="Pro Monthly"
                        data-testid="plan-name-input"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={planForm.description}
                        onChange={(e) => setPlanForm({ ...planForm, description: e.target.value })}
                        placeholder="Access to all courses..."
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Price (GBP) *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={planForm.price}
                          onChange={(e) => setPlanForm({ ...planForm, price: parseFloat(e.target.value) || 0 })}
                          data-testid="plan-price-input"
                        />
                      </div>
                      <div>
                        <Label>Billing Cycle</Label>
                        <Select 
                          value={planForm.billing_cycle} 
                          onValueChange={(v) => setPlanForm({ ...planForm, billing_cycle: v })}
                        >
                          <SelectTrigger data-testid="plan-cycle-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="annual">Annual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Features (one per line)</Label>
                      <Textarea
                        value={planForm.features}
                        onChange={(e) => setPlanForm({ ...planForm, features: e.target.value })}
                        placeholder="Access to all courses&#10;Priority support&#10;Certificates included"
                        rows={4}
                      />
                    </div>
                    <Button 
                      className="w-full bg-violet-700 hover:bg-violet-800"
                      onClick={handleCreatePlan}
                      data-testid="submit-plan-btn"
                    >
                      Create Plan
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {plans.length === 0 ? (
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardContent className="p-12 text-center">
                  <Calendar className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-stone-900 mb-2">No subscription plans</h3>
                  <p className="text-stone-500">Create subscription plans for recurring revenue</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {plans.map((plan) => (
                  <Card key={plan.plan_id} className="bg-white rounded-xl border border-stone-100">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle style={{ fontFamily: 'Outfit' }}>{plan.name}</CardTitle>
                          <Badge variant="secondary" className="mt-2 capitalize">
                            {plan.billing_cycle}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeletePlan(plan.plan_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-stone-900 mb-4" style={{ fontFamily: 'Outfit' }}>
                        £{plan.price.toFixed(2)}
                        <span className="text-sm font-normal text-stone-500">
                          /{plan.billing_cycle === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      </div>
                      {plan.description && (
                        <p className="text-stone-600 text-sm mb-4">{plan.description}</p>
                      )}
                      {plan.features?.length > 0 && (
                        <ul className="space-y-2">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-stone-600">
                              <CheckCircle className="h-4 w-4 text-emerald-500" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Course Bundles Tab */}
          <TabsContent value="bundles">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'Outfit' }}>
                Course Bundles ({bundles.length})
              </h2>
              <Dialog open={isBundleOpen} onOpenChange={setIsBundleOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-violet-700 hover:bg-violet-800 rounded-full" data-testid="create-bundle-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Bundle
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Outfit' }}>Create Course Bundle</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                    <div>
                      <Label>Bundle Name *</Label>
                      <Input
                        value={bundleForm.name}
                        onChange={(e) => setBundleForm({ ...bundleForm, name: e.target.value })}
                        placeholder="Web Development Bundle"
                        data-testid="bundle-name-input"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={bundleForm.description}
                        onChange={(e) => setBundleForm({ ...bundleForm, description: e.target.value })}
                        placeholder="Learn web development from scratch..."
                        rows={2}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Bundle Price (GBP) *</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={bundleForm.price}
                          onChange={(e) => setBundleForm({ ...bundleForm, price: parseFloat(e.target.value) || 0 })}
                          data-testid="bundle-price-input"
                        />
                      </div>
                      <div>
                        <Label>Discount %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={bundleForm.discount_percent}
                          onChange={(e) => setBundleForm({ ...bundleForm, discount_percent: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Select Courses *</Label>
                      <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-lg p-3">
                        {courses.map(course => (
                          <label 
                            key={course.course_id}
                            className="flex items-center gap-3 p-2 hover:bg-stone-50 rounded-lg cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={bundleForm.course_ids.includes(course.course_id)}
                              onChange={() => toggleCourseInBundle(course.course_id)}
                              className="w-4 h-4 text-violet-700"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{course.title}</p>
                              <p className="text-xs text-stone-500">£{course.price?.toFixed(2) || '0.00'}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                      <p className="text-xs text-stone-500 mt-1">
                        {bundleForm.course_ids.length} courses selected
                      </p>
                    </div>
                    <Button 
                      className="w-full bg-violet-700 hover:bg-violet-800"
                      onClick={handleCreateBundle}
                      data-testid="submit-bundle-btn"
                    >
                      Create Bundle
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {bundles.length === 0 ? (
              <Card className="bg-white rounded-xl border border-stone-100">
                <CardContent className="p-12 text-center">
                  <Package className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium text-stone-900 mb-2">No bundles yet</h3>
                  <p className="text-stone-500">Create course bundles to offer discounts</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-6">
                {bundles.map((bundle) => (
                  <Card key={bundle.bundle_id} className="bg-white rounded-xl border border-stone-100">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle style={{ fontFamily: 'Outfit' }}>{bundle.name}</CardTitle>
                          <p className="text-sm text-stone-500 mt-1">
                            {bundle.courses?.length || 0} courses included
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDeleteBundle(bundle.bundle_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {bundle.description && (
                        <p className="text-stone-600 text-sm mb-4">{bundle.description}</p>
                      )}
                      <div className="flex items-baseline gap-2 mb-4">
                        <span className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>
                          £{bundle.price.toFixed(2)}
                        </span>
                        {bundle.original_price > bundle.price && (
                          <span className="text-lg text-stone-400 line-through">
                            £{bundle.original_price.toFixed(2)}
                          </span>
                        )}
                        {bundle.discount_percent > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            {bundle.discount_percent}% off
                          </Badge>
                        )}
                      </div>
                      <div className="space-y-1">
                        {bundle.courses?.slice(0, 3).map(course => (
                          <p key={course.course_id} className="text-sm text-stone-600 truncate">
                            • {course.title}
                          </p>
                        ))}
                        {bundle.courses?.length > 3 && (
                          <p className="text-sm text-stone-400">
                            +{bundle.courses.length - 3} more courses
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdminSubscriptions;
